// Share a baby's tracker via an invite link. The link points at /join/<token>;
// opening it makes the recipient a caretaker. Reuses a live invite token if one
// exists, else mints one. Only the creator can mint invites (RLS enforces it).

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { generateBabyId } from "../lib/babyId";
import { useT } from "../lib/LocaleContext";

type Props = {
  open: boolean;
  babyId: string;
  babyName: string;
  onClose: () => void;
};

export function ShareSheet({ open, babyId, babyName, onClose }: Props) {
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const t = useT();

  useEffect(() => {
    // NB: `loading` must NOT be in the guard or deps — setLoading(true) below
    // would re-run this effect and cancel its own in-flight request.
    if (!open || inviteUrl) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Reuse a still-valid invite if there is one, else mint a fresh token.
        const { data: existing } = await supabase
          .from("baby_invites")
          .select("token, expires_at")
          .eq("baby_id", babyId)
          .gt("expires_at", new Date().toISOString())
          .order("created_at", { ascending: false })
          .limit(1);

        let token = existing?.[0]?.token as string | undefined;
        if (!token) {
          token = generateBabyId(24);
          const { data: s } = await supabase.auth.getSession();
          const { error: insErr } = await supabase
            .from("baby_invites")
            .insert({ token, baby_id: babyId, created_by: s.session?.user?.id });
          if (insErr) throw insErr;
        }
        if (!cancelled) setInviteUrl(`${window.location.origin}/babyhelper/join/${token}`);
      } catch {
        if (!cancelled) setError(t.share.creatorOnly);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, babyId, inviteUrl, t]);

  if (!open) return null;

  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function copy() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is shown above to copy by hand */
    }
  }

  async function shareNative() {
    if (!inviteUrl) return;
    try {
      await navigator.share({ title: t.share.shareTitle(babyName), text: t.share.shareText(babyName), url: inviteUrl });
    } catch {
      /* user cancelled — no-op */
    }
  }

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <h2 style={styles.title}>{t.share.title}</h2>
        <p style={styles.body}>{t.share.body(babyName)}</p>

        {loading && <div style={styles.pending}>{t.share.preparing}</div>}
        {error && <div style={styles.error}>{error}</div>}
        {inviteUrl && (
          <div className="bh-num" style={styles.linkBox}>
            {inviteUrl}
          </div>
        )}

        {inviteUrl && canShare && (
          <button style={styles.primary} onClick={shareNative}>
            {t.share.shareEllipsis}
          </button>
        )}
        <div style={styles.actions}>
          <button style={styles.ghost} onClick={onClose}>
            {t.share.close}
          </button>
          {inviteUrl && (
            <button style={canShare ? styles.ghost : styles.primary} onClick={copy}>
              {copied ? t.share.copied : t.share.copyLink}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 110,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(20px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
  },
  handle: { width: 40, height: 4, background: "var(--bh-divider)", borderRadius: 2, margin: "0 auto 14px" },
  title: { fontSize: 20, fontWeight: 600, margin: "0 0 8px", color: "var(--bh-ink)" },
  body: { fontSize: 14, lineHeight: 1.5, color: "var(--bh-ink-3)", margin: "0 0 14px" },
  pending: { fontSize: 14, color: "var(--bh-ink-3)", padding: "10px 0 14px" },
  linkBox: {
    background: "var(--bh-surface-2)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 13,
    color: "var(--bh-ink-2)",
    wordBreak: "break-all",
    marginBottom: 12,
    userSelect: "all",
  },
  error: {
    background: "var(--bh-attn-soft)",
    color: "var(--bh-attn)",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    marginBottom: 12,
  },
  actions: { display: "flex", gap: 8, marginTop: 8 },
  primary: {
    width: "100%",
    flex: 1,
    background: "var(--bh-feed)",
    color: "oklch(0.2 0.04 70)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
};
